
import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders } from '@angular/common/http';
import { AI_BOT_CONFIG, BASE_DOMAIN_URI } from 'app/app.config';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LlmService {

  model1_base_url = `${AI_BOT_CONFIG.training.model1Url}` + "/legal_document/";
  model2_base_url = `${AI_BOT_CONFIG.training.model2Url}` + "/absenceassistance-library/";
  model3_base_url = `${AI_BOT_CONFIG.training.model3Url}`;
  model4_base_url = `${AI_BOT_CONFIG.training.model4Url}`;


  Disclaimer = "<b>Disclaimer:</b> <span>The information provided from this tool is not for legal advice, and is intended to provide references.</span>";

  LLM_classes: any[] = [];
  LLM_user_list: any[] = [];

  selectedDoc_ids: any[] = [];
  selectedDoc_name_map: any = {};
  selectedDoc_summary_category_map: any = {};

  llm_bot_payload: any = {
    mode: '',
    class_id: '',
    model_type: '',
    document_id: []
  };

  private jsonHeaders = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(private http: HttpClient) {
    this.restoreLLMBotPayload();
    this.restoreSelectedDocs();
  }

  // ===============================
  // LocalStorage Functions
  // ===============================

  saveLLMBotPayload() {
    try {
      localStorage.setItem('llm_bot_payload', JSON.stringify(this.llm_bot_payload));
    } catch (error) {
      console.error('Error saving llm_bot_payload:', error);
    }
  }

  restoreLLMBotPayload() {
    try {
      const savedPayload = localStorage.getItem('llm_bot_payload');

      if (!savedPayload) return;

      const parsed = JSON.parse(savedPayload);

      if (parsed && typeof parsed === 'object') {
        this.llm_bot_payload = {
          mode: parsed.mode ?? '',
          class_id: parsed.class_id ?? '',
          model_type: parsed.model_type ?? '',
          document_id: parsed.document_id ?? []
        };
      }

    } catch (error) {
      console.error('Error restoring llm_bot_payload:', error);
    }
  }

  clearLLMBotPayload() {
    try {
      localStorage.removeItem('llm_bot_payload');

      this.llm_bot_payload = {
        mode: '',
        class_id: '',
        model_type: '',
        document_id: []
      };

    } catch (error) {
      console.error('Error clearing llm_bot_payload:', error);
    }
  }

  saveSelectedDocs() {
    try {
      const data = {
        selectedDoc_ids: this.selectedDoc_ids,
        selectedDoc_name_map: this.selectedDoc_name_map,
        selectedDoc_summary_category_map: this.selectedDoc_summary_category_map
      };

      localStorage.setItem('llm_selected_docs', JSON.stringify(data));

    } catch (error) {
      console.error('Error saving selected docs:', error);
    }
  }

  restoreSelectedDocs() {
    try {
      const saved = localStorage.getItem('llm_selected_docs');

      if (!saved) return;

      const parsed = JSON.parse(saved);

      if (parsed) {
        this.selectedDoc_ids = parsed.selectedDoc_ids || [];
        this.selectedDoc_name_map = parsed.selectedDoc_name_map || {};
        this.selectedDoc_summary_category_map = parsed.selectedDoc_summary_category_map || {};
      }

    } catch (error) {
      console.error('Error restoring selected docs:', error);
    }
  }

  clearSelectedDocs() {
    try {

      localStorage.removeItem('llm_selected_docs');

      this.selectedDoc_ids = [];
      this.selectedDoc_name_map = {};
      this.selectedDoc_summary_category_map = {};

    } catch (error) {
      console.error('Error clearing selected docs:', error);
    }
  }

  // ===============================
  // User APIs
  // ===============================

  llmGenerateSessionId() {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-API-Key': 'canstring@benefits-agent'
    });

    return this.http.post(
      `${this.model4_base_url}/session`,
      {},
      { headers }
    );
  }

  llmCreateUser(payload: any) {
    return this.http.post(this.model2_base_url + 'create-user', payload);
  }

  llmUserLogin(payload: any) {
    return this.http.post(this.model2_base_url + 'login', payload);
  }

  llmUserLogout(payload: any) {
    return this.http.post(this.model2_base_url + 'logout', payload);
  }

  llmUserAddClassAccess(payload: any) {
    return this.http.post(this.model2_base_url + 'grant-class-access', payload);
  }

  llmUserRemoveClassAccess(payload: any) {
    return this.http.post(this.model2_base_url + 'revoke-class-access', payload);
  }

  llmUserInfo() {
    return this.http.get(this.model2_base_url + 'user-info');
  }

  llmUserList() {
    const payload = { client_id: localStorage.getItem("company_id") };
    return this.http.post(this.model2_base_url + 'list-users', payload);
  }

  // ===============================
  // Document APIs
  // ===============================

  llmTrainDoc(formData: any) {
    return this.http.post(this.model2_base_url + 'receive_document', formData);
  }

  llmUploadDocAss(formData: any) {
    return this.http.post(this.model2_base_url + 'update', formData);
  }

  llmTrainDocUpdate(formData: any, llm_unqId: string) {
    return this.http.put(this.model2_base_url + 'update-document/' + llm_unqId, formData);
  }

  llmTrainDocDelete(llm_unqId: string) {
    return this.http.delete(this.model2_base_url + 'delete_document/' + llm_unqId);
  }

  // ===============================
  // Query APIs
  // ===============================

  llmQuery(payload: any) {
    return this.http.post(this.model2_base_url + 'query', payload, this.jsonHeaders);
  }

  llmChatQuery(payload: any) {
    return this.http.post(this.model3_base_url + '/chat', payload, this.jsonHeaders)
  }

  llmQuery_v2(payload: any) {
    return this.http.post(this.model2_base_url + 'query_v2', payload, this.jsonHeaders);
  }

  // llmQuery_v2_stream(payload: any): Observable<HttpEvent<any>> {
  //   return this.http.post(this.model2_base_url + 'query_v2_stream', payload, {
  //     observe: 'events',
  //     reportProgress: true,
  //     responseType: 'text'
  //   })
  // }

  //Private
  streamPrivateQuery(payload: any): Observable<any> {
    return this.streamSSE(
      this.model2_base_url + 'query_v2_stream',
      payload
    );
  }

  streamPrivateQuery1(payload: any): Observable<any> {
    return this.streamSSE(
      this.model4_base_url + '/chat'
      , payload)
  }

  //Generic
  streamGenericQuery(payload: any): Observable<any> {
    return this.streamSSE(
      this.model2_base_url + 'generic_query_stream',
      payload
    );
  }


  private streamSSE(url: string, payload: any): Observable<any> {

    return new Observable(observer => {

      const controller = new AbortController();

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('llm_token')}`,
          'X-API-Key': `canstring@benefits-agent`
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      })
        .then(async response => {

          if (!response.ok) {

            let error: any = {};

            try {
              error = await response.json();
            } catch { }

            observer.error(error.error || `HTTP ${response.status}`);
            return;
          }

          if (!response.body) {
            observer.error('Streaming response is empty.');
            return;
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();

          let buffer = '';

          while (true) {

            const { done, value } = await reader.read();

            if (done) {

              // Process any remaining buffered event
              if (buffer.trim()) {
                this.processSSEEvent(buffer, observer);
              }

              observer.complete();
              break;
            }

            buffer += decoder.decode(value, { stream: true });

            const events = buffer.split(/\r?\n\r?\n/);
            buffer = events.pop() || '';

            for (const event of events) {
              this.processSSEEvent(event, observer);
            }
          }

        })
        .catch(error => {

          if (error.name !== 'AbortError') {
            observer.error(error);
          }

        });

      return () => controller.abort();

    });

  }

  private processSSEEvent(eventText: string, observer: any): void {

    const lines = eventText.split(/\r?\n/);

    let eventType = 'message';
    let dataString = '';

    for (const line of lines) {

      if (line.startsWith('event:')) {
        eventType = line.substring(6).trim();
      }

      if (line.startsWith('data:')) {
        dataString += line.substring(5).trim();
      }

    }

    if (!dataString) {
      return;
    }

    try {

      observer.next({
        event: eventType,
        data: JSON.parse(dataString)
      });

    } catch {

      observer.next({
        event: eventType,
        data: dataString
      });

    }

  }


  // streamGenericQuery(payload: any): Observable<any> {

  //   return new Observable(observer => {

  //     fetch(this.model2_base_url + 'generic_query_stream', {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         'authorization': `Bearer ${localStorage.getItem('llm_token')}`
  //       },
  //       body: JSON.stringify(payload)

  //     }).then(async (response: any) => {

  //       const reader = response.body.getReader();
  //       const decoder = new TextDecoder();

  //       let buffer = '';

  //       while (true) {

  //         const { done, value } = await reader.read();

  //         if (done) {
  //           observer.complete();
  //           break;
  //         }

  //         buffer += decoder.decode(value, { stream: true });

  //         // Split each SSE message
  //         const events = buffer.split(/\r?\n\r?\n/);
  //         buffer = events.pop() || '';

  //         for (const evt of events) {

  //           const lines = evt.split(/\r?\n/);

  //           let eventType = '';
  //           let dataString = '';

  //           for (const line of lines) {

  //             if (line.startsWith('event:')) {
  //               eventType = line.substring(6).trim();
  //             }

  //             if (line.startsWith('data:')) {
  //               dataString += line.substring(5).trim();
  //             }

  //           }

  //           if (!dataString) {
  //             continue;
  //           }

  //           let data: any;

  //           try {
  //             data = JSON.parse(dataString);
  //           } catch {
  //             data = {
  //               html: dataString
  //             };
  //           }

  //           observer.next({
  //             event: eventType,
  //             data
  //           });

  //         }

  //       }

  //     }).catch(error => observer.error(error));

  //   });

  // }

  llmGenericQuery(payload: any) {
    return this.http.post(this.model2_base_url + 'generic_query', payload, this.jsonHeaders);
  }

  llmSimpleQuery(payload: any) {
    return this.http.post(this.model2_base_url + 'query_simple', payload, this.jsonHeaders);
  }

  llmSimpleQuery_v2(payload: any) {
    return this.http.post(this.model2_base_url + 'simple_query_v2', payload, this.jsonHeaders);
  }

  llmAnalyzeQuery(payload: any) {
    return this.http.post(this.model2_base_url + 'analyze-query', payload, this.jsonHeaders);
  }

  llmAdvancedQuery_v2(payload: any) {
    return this.http.post(this.model1_base_url + 'detailed_query', payload, this.jsonHeaders);
  }

  // ===============================
  // Bharathi LLM
  // ===============================

  llmsearchAndQAUpload(formData: any) {
    return this.http.post(this.model1_base_url + 'upload', formData);
  }



  llmsearchAndQAUploadSearch(formData: any) {
    return this.http.post(this.model1_base_url + 'query', formData);
  }

  llmsearchBydocId(doc_id: string) {
    return this.http.get(this.model1_base_url + 'documents/' + doc_id);
  }

  llmdeleteBydocId(doc_id: string) {
    return this.http.delete(this.model1_base_url + 'delete/' + doc_id);
  }

  // ===============================
  // Class APIs
  // ===============================

  llmDeleteClass(classid: string) {
    const payload = { class_id: classid };
    return this.http.post(this.model2_base_url + 'delete-class', payload);
  }

  llmClassInfo(param: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'authentication': localStorage.getItem('llm_token') || ''
    });

    return this.http.get(
      `${this.model2_base_url}class-info?class_id=${encodeURIComponent(param)}`,
      { headers }
    );
  }

  // ===============================
  // Assessment APIs
  // ===============================

  llmQuestionGenerate(payload: any) {
    return this.http.post(this.model2_base_url + 'generate-assessment', payload);
  }

  llmQuestionGeneratebyDoc(payload: any) {
    return this.http.post(this.model2_base_url + 'generate-document-assessment', payload);
  }

  // ===============================
  // Health API
  // ===============================

  llmRunningStarus() {
    return this.http.get(this.model2_base_url + 'health');
  }

  // ===============================
  // RFP Upload
  // ===============================

  llmRfpQAUpload(formData: any) {
    return this.http.post(
      BASE_DOMAIN_URI + '/pdf/rfp_q_a',
      formData,
      { responseType: 'blob', observe: 'response' }
    );
  }

  deleteTrainDocModel4(documentId: any) {

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-API-Key': 'canstring@benefits-agent'
    });

    return this.http.delete(
      this.model4_base_url + `/benefits-chat/delete/${documentId}?tenant_id=5001&region=TEST`, { headers }
    )
  }

  //Meet Model new API for generic search

  benfitGenericStreaming(payload: any) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-API-Key': 'canstring@benefits-agent'
    });

    return this.streamSSE(
      this.model4_base_url + '/generic_query'
      , payload)

    // return this.http.post(
    //   `${this.model4_base_url}/generic_query`,
    //   payload,
    //   { headers }
    // );
  }

}
